import { companyZone } from "./zone";
import { el } from "./dom";
import { icon } from "./icons";

// Autosave (brief 1A-6, mockup .autosave): a form saves on its own a short pause after the last
// change, says "Saving…" and then "Saved" with the time. A failed save is tried once more; if
// that fails too it says plainly that the change wasn't saved, and the next change tries again.
// The timing is worked out in createAutosave (no page needed), the words by autosaveText, and
// autosaveStatus draws them.

export type SaveResult = { ok: true } | { ok: false; error?: string };

export type AutosaveStatus =
  | { state: "idle" }
  | { state: "waiting" }
  | { state: "saving" }
  | { state: "saved"; at: Date }
  | { state: "failed"; error?: string };

export interface Timers {
  set: (fn: () => void, ms: number) => unknown;
  clear: (handle: unknown) => void;
  now: () => Date;
}

const REAL: Timers = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
  now: () => new Date(),
};

/** How long after the last change it saves, and how long before the one retry. */
export const PAUSE_MS = 800;
export const RETRY_MS = 1500;

export interface Autosave {
  /** Call on every change; the save runs after a pause with no further change. */
  changed: () => void;
  /** Saves now if a change is waiting (leaving the screen). */
  flush: () => Promise<void>;
}

export function createAutosave(opts: {
  save: () => Promise<SaveResult>;
  onStatus: (s: AutosaveStatus) => void;
  onSaved?: () => void | Promise<void>;
  timers?: Timers;
}): Autosave {
  const t = opts.timers ?? REAL;
  let timer: unknown = null;
  let running: Promise<void> | null = null;
  let again = false;

  async function attempt(): Promise<SaveResult> {
    try {
      return await opts.save();
    } catch {
      return { ok: false, error: "the connection dropped" };
    }
  }

  async function run(): Promise<void> {
    opts.onStatus({ state: "saving" });
    let res = await attempt();
    if (!res.ok) {
      await new Promise<void>((done) => t.set(done, RETRY_MS));
      res = await attempt();
    }
    if (res.ok) {
      opts.onStatus({ state: "saved", at: t.now() });
      await opts.onSaved?.();
    } else {
      opts.onStatus({ state: "failed", error: res.error });
    }
  }

  async function go(): Promise<void> {
    timer = null;
    if (running) {
      // A change came in while saving: save once more when this one is done.
      again = true;
      return running;
    }
    running = run();
    await running;
    running = null;
    if (again) {
      again = false;
      await go();
    }
  }

  return {
    changed() {
      if (timer !== null) t.clear(timer);
      opts.onStatus({ state: "waiting" });
      timer = t.set(() => void go(), PAUSE_MS);
    },
    async flush() {
      if (timer === null) return running ?? undefined;
      t.clear(timer);
      await go();
    },
  };
}

const clock = (at: Date) =>
  new Intl.DateTimeFormat("en-US", {
    timeZone: companyZone(),
    hour: "numeric",
    minute: "2-digit",
  }).format(at);

/** What the status line says. */
export function autosaveText(s: AutosaveStatus): string {
  switch (s.state) {
    case "idle":
      return "Saves automatically";
    case "waiting":
    case "saving":
      return "Saving…";
    case "saved":
      return `Saved ${clock(s.at)}`;
    case "failed":
      return `Not saved${s.error ? `: ${s.error}` : ""}. The change is still here and saves again on the next edit.`;
  }
}

/** The status line (mockup .autosave): a check and "Saved 3:04 PM", or a plain warning. */
export function autosaveStatus(): { el: HTMLElement; show: (s: AutosaveStatus) => void } {
  const out = el("span", { class: "autosave", role: "status", "aria-live": "polite" });
  const show = (s: AutosaveStatus) => {
    out.dataset.state = s.state;
    out.classList.toggle("bad", s.state === "failed");
    const ic = s.state === "failed" ? "alert" : s.state === "saved" ? "check" : "sync";
    out.replaceChildren(icon(ic), ` ${autosaveText(s)}`);
  };
  show({ state: "idle" });
  return { el: out, show };
}
